import React, { useState, useEffect, useMemo, useRef } from "react";
import { ThreeDots } from "react-loader-spinner";
import "../../Pages/Admin/Style.css";
import "./add_banner.css";
import { TailSpin } from "react-loader-spinner";
import Select from "react-select";
import "../../Api/apiUrls.js";
import {
  addArtWorks,
  getArtWorkKeywords,
  getArtWorksByUserId,
  getArtWorksById,
  updateArtWorks,
  deleteArtWorksById,
  API_URL,
} from "../../Api/apiUrls";

// Helper functions
const parseKeywordParts = (input) =>
  String(input || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const normalizeKeywordText = (raw) => {
  if (Array.isArray(raw)) {
    return raw
      .map((item) => String(item).trim())
      .filter(Boolean)
      .join(", ");
  }

  const source = String(raw ?? "").trim();
  if (!source) return "";

  if (source.startsWith("[") && source.endsWith("]")) {
    const matches = Array.from(
      source.matchAll(/'([^']+)'|"([^"]+)"|([^\[\],]+)/g)
    )
      .map((m) => (m[1] || m[2] || m[3] || "").trim())
      .filter(Boolean);
    if (matches.length > 0) return matches.join(", ");
  }

  return parseKeywordParts(source).join(", ");
};

const normalizeKeywordOptions = (raw) => {
  if (!Array.isArray(raw)) return [];
  const set = new Set();
  raw.forEach((item) => {
    parseKeywordParts(normalizeKeywordText(item)).forEach((kw) => set.add(kw));
  });
  return Array.from(set);
};

// KeywordInput Component with dropdown and chips
const KeywordInput = ({ 
  id, 
  value, 
  onChange, 
  placeholder, 
  allKeywords, 
  compact 
}) => {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Parse selected keywords from value prop
  const selectedKeywords = useMemo(() => {
    if (!value) return [];
    return parseKeywordParts(value);
  }, [value]);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.trim()) {
      const filtered = allKeywords
        .filter(kw => 
          kw.toLowerCase().includes(inputValue.toLowerCase()) &&
          !selectedKeywords.includes(kw)
        )
        .slice(0, 10);
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, allKeywords, selectedKeywords]);

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Add a new keyword
  const addKeyword = (keyword) => {
    const trimmed = keyword.trim();
    if (!trimmed) return;
    
    if (!selectedKeywords.includes(trimmed)) {
      const newKeywords = [...selectedKeywords, trimmed];
      onChange(newKeywords.join(", "));
    }
    setInputValue("");
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  // Remove a keyword
  const removeKeyword = (keywordToRemove) => {
    const newKeywords = selectedKeywords.filter(kw => kw !== keywordToRemove);
    onChange(newKeywords.join(", "));
  };

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    // Check if last character is comma
    if (value.endsWith(',')) {
      const keywordToAdd = value.slice(0, -1).trim();
      if (keywordToAdd) {
        addKeyword(keywordToAdd);
      }
    }
  };

  // Handle key down events
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (inputValue.trim()) {
        addKeyword(inputValue);
      }
    } else if (e.key === 'Backspace' && inputValue === '' && selectedKeywords.length > 0) {
      // Remove last keyword on backspace when input is empty
      const newKeywords = selectedKeywords.slice(0, -1);
      onChange(newKeywords.join(", "));
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    addKeyword(suggestion);
  };

  const chipPad = compact ? "4px 10px" : "6px 14px";
  const chipFont = compact ? "0.75rem" : "0.8125rem";
  const chipGap = compact ? 6 : 8;

  return (
    <div className="form-group mb-0" style={{ position: "relative" }} ref={wrapperRef}>
      <label className="small mb-1" htmlFor={id}>
        Keyword Heading
      </label>
      
      {/* Selected Keywords Chips */}
      {selectedKeywords.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: chipGap,
            marginBottom: 8,
            padding: "4px 0",
          }}
        >
          {selectedKeywords.map((keyword) => (
            <div
              key={keyword}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                borderRadius: 999,
                padding: chipPad,
                fontSize: chipFont,
                lineHeight: 1.2,
                border: "1px solid #0d6efd",
                background: "#e7f1ff",
                color: "#0a58ca",
                fontWeight: 600,
              }}
            >
              <span>{keyword}</span>
              <button
                type="button"
                onClick={() => removeKeyword(keyword)}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  margin: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                  color: "#0a58ca",
                  fontSize: "14px",
                }}
                aria-label={`Remove ${keyword}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      
      {/* Input Field */}
      <input
        ref={inputRef}
        className={compact ? "form-control form-control-sm" : "form-control"}
        id={id}
        type="text"
        autoComplete="off"
        aria-label="Enter keywords"
        value={inputValue}
        placeholder={placeholder || "Type keyword and press Enter or comma"}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => inputValue.trim() && setShowSuggestions(filteredSuggestions.length > 0)}
      />
      
      {/* Suggestions Dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: "#fff",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            maxHeight: "200px",
            overflowY: "auto",
            marginTop: "4px",
          }}
        >
          {filteredSuggestions.map((suggestion) => (
            <div
              key={suggestion}
              onClick={() => handleSuggestionClick(suggestion)}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontSize: "0.875rem",
                borderBottom: "1px solid #f0f0f0",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f8f9fa";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
              }}
            >
              {suggestion}
            </div>
          ))}
        </div>
      )}
      
      {/* Help Text */}
      <small className="text-muted" style={{ display: "block", marginTop: "4px", fontSize: "0.7rem" }}>
        Type keyword and press Enter or comma to add. Click on chips to remove.
      </small>
    </div>
  );
};

function Add_Art_Work({ onClose, instructorId }) {
  const [artWork, setArtWork] = useState([]);
  const [sequence, setSequence] = useState("");
  const [rate, setRate] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [artWorkId, setArtWorkId] = useState();
  const [modalAdd_Art_WorkIsOpen, setAdd_Art_WorkModalIsOpen] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isAddingArtWork, setIsAddingArtWork] = useState(false);
  const [isUpdatingArtWork, setIsUpdatingArtWork] = useState(false);
  const [newSequence, setNewSequence] = useState("");
  const [newRate, setNewRate] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newStatus, setNewStatus] = useState(false);
  const [showFields, setShowFields] = useState(false);
  const [artWorkStatus, setArtWorkStatus] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const fileInputRef = React.createRef();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [artWorkToDelete, setArtWorkToDelete] = useState(null);
  const [keywordHeading, setKeywordHeading] = useState("");
  const [newKeywordHeading, setNewKeywordHeading] = useState("");
  const [artWorkKeywords, setArtWorkKeywords] = useState([]);
  const [addKeywordFieldKey, setAddKeywordFieldKey] = useState(0);

  const closeAdd_Art_Work = () => {
    setAdd_Art_WorkModalIsOpen(false);
  };
  
  const handleCancelClick = () => {
    setShowFields(false);
  };

  const handleClick = (e) => {
    if (e.currentTarget === e.target) {
      onClose();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClick);

    return () => {
      document.removeEventListener("click", handleClick);
    };
  }, [handleClick]);
  
  const Status = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Approve" },
    { value: "-1", label: "Reject" },
  ];

  const handleStatusChange = (selectedOption) => {
    setArtWorkStatus(selectedOption.value);
  };

  // Allow only numbers for rate and sequence
  const handleRateChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setRate(value);
    }
  };

  const handleSequenceChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setSequence(value);
    }
  };

  const handleNewRateChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setNewRate(value);
    }
  };

  const handleNewSequenceChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setNewSequence(value);
    }
  };

  const [iconFile, setIconFile] = useState([]);

  const resetAddFormFields = () => {
    setIconFile([]);
    setTitle("");
    setDescription("");
    setRate("");
    setSequence("");
    setKeywordHeading("");
    setAddKeywordFieldKey((k) => k + 1);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const cancelAddForm = () => {
    resetAddFormFields();
    setShowAddForm(false);
  };

  const handleHeaderCloseClick = (e) => {
    e.stopPropagation();
    if (showFields) {
      handleCancelClick();
      return;
    }
    if (showAddForm) {
      cancelAddForm();
      return;
    }
    onClose();
  };
  
  const handleAdd_Art_Work = async (event) => {
    try {
      event.preventDefault();
  
      const files = iconFile;
      if (!files || files.length === 0) {
        alert("No files selected");
        return;
      }
  
      setIsAddingArtWork(true);
  
      const formData = new FormData();
      formData.append("user", instructorId);
      formData.append("title", title || "");
      formData.append("description", description || "");
      formData.append("rate", rate || "");
      formData.append("sequence", sequence || "");
      formData.append("keywords", keywordHeading);
  
      files.forEach((file) => {
        formData.append("photo", file);
      });
  
      const addResponse = await fetch(`${addArtWorks}`, {
        method: "POST",
        body: formData,
      });
  
      if (!addResponse.ok) {
        alert("Failed to add Art Work");
        return;
      }
  
      await fetchArtWork();
      cancelAddForm();
    } catch (error) {
      console.error("Error handling Art Work:", error);
    } finally {
      setIsAddingArtWork(false);
    }
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files); 
    setIconFile(files); 
  };

  const fetchArtWork = async () => {
    try {
      const response = await fetch(`${getArtWorksByUserId}${instructorId}`);
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();
      const artWorkData = (data.data.ArtWorks || []).map((item) => ({
        ...item,
        keywords: normalizeKeywordText(item.keywords),
      }));
      setArtWork(artWorkData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchArtWork();

    const handleArtWorkUpdated = () => {
      fetchArtWork();
    };

    window.addEventListener('artWorkUpdated', handleArtWorkUpdated);

    return () => {
      window.removeEventListener('artWorkUpdated', handleArtWorkUpdated);
    };
  }, [getArtWorksByUserId, instructorId]);

  useEffect(() => {
    let cancelled = false;
    const loadKeywords = async () => {
      try {
        const res = await fetch(getArtWorkKeywords);
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const raw = json?.data?.keywords ?? json?.keywords ?? [];
        const list = normalizeKeywordOptions(raw);
        if (!cancelled) setArtWorkKeywords(list);
      } catch (e) {
        console.error("Failed to load art work keywords:", e);
      }
    };
    loadKeywords();
    return () => {
      cancelled = true;
    };
  }, []);
  
  const handleClickEdit = (artWorkId) => {
    fetch(`${getArtWorksById}${artWorkId}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        const artWorkData = data.data.ArtWorks;
        setNewSequence(artWorkData.sequence || "");
        setNewRate(artWorkData.rate || "");
        setNewTitle(artWorkData.title || "");
        setNewDescription(artWorkData.description || "");
        setNewKeywordHeading(normalizeKeywordText(artWorkData.keywords));
        setArtWorkStatus(artWorkData.status?.toString() || "0");
        setArtWorkId(artWorkData.id);
      })
      .catch((error) => console.error("Error fetching data:", error))
      .finally(() => {
        setIsLoading(false);
      });

    setShowFields(true);
  };
  
  const handleUpdateArtWork = async () => {
    try {
      setIsUpdatingArtWork(true);

      const formData = new FormData();
      formData.append("art_work_id", artWorkId);
      formData.append("sequence", newSequence);
      formData.append("rate", newRate);
      formData.append("title", newTitle);
      formData.append("description", newDescription);
      formData.append("status", artWorkStatus);
      formData.append("keywords", newKeywordHeading);

      const updateResponse = await fetch(`${updateArtWorks}`, {
        method: "PUT",
        body: formData,
      });

      if (!updateResponse.ok) {
        alert("Failed to update Art Work");
        return;
      }

      await fetchArtWork();
      setShowFields(false);
    } catch (error) {
      console.error("Error handling Art Work:", error);
    } finally {
      setIsUpdatingArtWork(false);
    }
  };

  // Delete confirmation handlers
  const confirmDelete = (artWorkId) => {
    setArtWorkToDelete(artWorkId);
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setArtWorkToDelete(null);
  };

  const handleDeleteArtWork = async () => {
    try {
      const response = await fetch(`${deleteArtWorksById}${artWorkToDelete}`, {
        method: "DELETE",
        headers: {
          accept: "application/json",
        },
      });

      if (!response.ok) {
        alert("Failed to delete Art Work");
        return;
      }

      await fetchArtWork();
      setShowDeleteConfirm(false);
      setArtWorkToDelete(null);
    } catch (error) {
      console.error("Error deleting Art Work:", error);
    }
  };

  return (
    <>
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="popup-background" style={{ zIndex: 1000 }}>
          <div className="popup" style={{ width: "400px", height: "200px" }}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  onClick={cancelDelete}
                  className="close"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this art work?</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDeleteArtWork}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="popup-background" onClick={handleClick}>
        <div
          className="popup"
          style={{
            position: "relative",
            top: "auto",
            left: "auto",
            marginLeft: 0,
            width: showFields ? "min(660px, 92vw)" : "min(720px, 92vw)",
            maxWidth: "100%",
            height: "auto",
            maxHeight: showFields ? "min(82vh, 580px)" : "min(85vh, 680px)",
            margin: "0 auto",
            alignSelf: "center",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 12px 40px rgba(15, 23, 42, 0.12)",
          }}
        >
          <div
            className="modal-content"
            style={{
              width: "100%",
              maxHeight: "100%",
              minHeight: 0,
              padding: 0,
              backgroundColor: "#fff",
              borderRadius: "8px",
              border: "1px solid #e8eaed",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              flex: "1 1 auto",
            }}
          >
            <div
              className="modal-header"
              style={{
                width: "100%",
                marginBottom: 0,
                padding: "10px 16px",
                borderBottom: "1px solid #e9ecef",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexShrink: 0,
                background: "#fafbfc",
              }}
            >
              <h5
                className="modal-title"
                id="modalAddTitle"
                style={{
                  marginBottom: 0,
                  fontSize: "1rem",
                  fontWeight: 600,
                  letterSpacing: "-0.01em",
                  color: "#212529",
                }}
              >
                {showFields ? "Edit Art Work" : "Art Works"}
              </h5>

              <button
                type="button"
                onClick={handleHeaderCloseClick}
                className="close"
                data-dismiss="modal"
                aria-label="Close"
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>

            <div
              className="modal-body"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                padding: "12px 16px",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              {showFields ? (
                <div>
                  <div className="form-group">
                    <label className="small mb-1" htmlFor="inputTitleEdit">
                      Title
                    </label>
                    <input
                      className="form-control"
                      id="inputTitleEdit"
                      type="text"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      placeholder="Enter art work title"
                    />
                  </div>

                  <div className="form-group">
                    <label className="small mb-1" htmlFor="inputDescriptionEdit">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="inputDescriptionEdit"
                      rows={3}
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      placeholder="Enter art work description"
                      style={{
                        resize: "vertical",
                        minHeight: "5.75rem",
                        lineHeight: 1.45,
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="small mb-1" htmlFor="inputRateEdit">
                      Rate (Numbers only)
                    </label>
                    <input
                      className="form-control"
                      id="inputRateEdit"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={newRate}
                      onChange={handleNewRateChange}
                      placeholder="Enter rate"
                      style={{ MozAppearance: "textfield" }}
                    />
                  </div>

                  <div className="form-group">
                    <label className="small mb-1" htmlFor="inputSequenceEdit">
                      Sequence (Numbers only)
                    </label>
                    <input
                      className="form-control"
                      id="inputSequenceEdit"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={newSequence}
                      onChange={handleNewSequenceChange}
                      placeholder="Enter sequence"
                      style={{ MozAppearance: "textfield" }}
                    />
                    <style>
                      {`
                        #inputRateEdit::-webkit-inner-spin-button,
                        #inputRateEdit::-webkit-outer-spin-button,
                        #inputSequenceEdit::-webkit-inner-spin-button,
                        #inputSequenceEdit::-webkit-outer-spin-button {
                          -webkit-appearance: none;
                          margin: 0;
                        }
                      `}
                    </style>
                  </div>

                  <KeywordInput
                    id="inputKeywordHeadingEdit"
                    value={newKeywordHeading}
                    onChange={setNewKeywordHeading}
                    placeholder="Enter keyword heading"
                    allKeywords={artWorkKeywords}
                  />

                  <div className="form-group">
                    <label className="small mb-1" htmlFor="inputStatusEdit">
                      Status
                    </label>
                    <Select
                      inputId="inputStatusEdit"
                      onChange={handleStatusChange}
                      options={Status}
                      placeholder="Select status"
                      value={Status.find((option) => option.value === artWorkStatus)}
                      classNamePrefix="artwork-select"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: 38,
                          fontSize: "1rem",
                          borderColor: "#ced4da",
                          boxShadow: "none",
                        }),
                        valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
                        indicatorsContainer: (base) => ({ ...base, height: 36 }),
                        placeholder: (base) => ({ ...base, fontSize: "1rem" }),
                        singleValue: (base) => ({ ...base, fontSize: "1rem" }),
                      }}
                    />
                  </div>

                  <div className="d-flex gap-2 pt-1">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      style={{ background: "#111", border: "none" }}
                      onClick={handleUpdateArtWork}
                      disabled={isUpdatingArtWork}
                    >
                      {isUpdatingArtWork ? "Updating..." : "Update"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleCancelClick}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {!showAddForm && (
                    <div style={{ marginBottom: 10 }}>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        style={{
                          background: "#111",
                          border: "none",
                          padding: "6px 12px",
                          fontSize: "0.8125rem",
                        }}
                        onClick={() => setShowAddForm(true)}
                      >
                        <i className="bi bi-plus-circle"></i> Add New Art Work
                      </button>
                    </div>
                  )}

                  {showAddForm && (
                    <form>
                      <div className="form-group">
                        <label className="small mb-1" htmlFor="inputDocument">
                          Art Work Image *
                        </label>
                        <br />
                        <input
                          type="file"
                          id="selectedImg"
                          name="photo"
                          accept="image/*"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          multiple
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label className="small mb-1" htmlFor="inputTitle">
                          Title
                        </label>
                        <input
                          className="form-control"
                          id="inputTitle"
                          type="text"
                          value={title}
                          onChange={(e) => setTitle(e.target.value)}
                          placeholder="Enter art work title"
                        />
                      </div>

                      <div className="form-group">
                        <label className="small mb-1" htmlFor="inputDescription">
                          Description
                        </label>
                        <textarea
                          className="form-control"
                          id="inputDescription"
                          rows="3"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Enter art work description"
                          style={{
                            resize: "vertical",
                            minHeight: "5.75rem",
                            lineHeight: 1.45,
                          }}
                        />
                      </div>

                      <div className="form-group">
                        <label className="small mb-1" htmlFor="inputRate">
                          Rate (Numbers only)
                        </label>
                        <input
                          className="form-control"
                          id="inputRate"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={rate}
                          onChange={handleRateChange}
                          placeholder="Enter rate"
                          style={{ MozAppearance: "textfield" }}
                        />
                      </div>

                      <div className="form-group">
                        <label className="small mb-1" htmlFor="inputSequence">
                          Sequence (Numbers only)
                        </label>
                        <input
                          className="form-control"
                          id="inputSequence"
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={sequence}
                          onChange={handleSequenceChange}
                          placeholder="Enter sequence"
                          style={{ MozAppearance: "textfield" }}
                        />
                      </div>

                      <KeywordInput
                        key={addKeywordFieldKey}
                        id="inputKeywordHeadingAdd"
                        value={keywordHeading}
                        onChange={setKeywordHeading}
                        placeholder="Enter keyword heading"
                        allKeywords={artWorkKeywords}
                      />

                      <div className="d-flex gap-2">
                        {isAddingArtWork === true ? (
                          <div>
                            <ThreeDots color="#000" height={30} width={30} />
                          </div>
                        ) : (
                          <>
                            <button
                              id="btnAdd"
                              type="button"
                              className="btn btn-primary btn-sm"
                              style={{ background: "#111", border: "none" }}
                              onClick={handleAdd_Art_Work}
                            >
                              Add
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm"
                              onClick={cancelAddForm}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </form>
                  )}
                  
                  {isLoading ? (
                    <div style={{ marginTop: 20, marginBottom: 16, textAlign: "center" }}>
                      <TailSpin color="black" width={40} />
                    </div>
                  ) : artWork && artWork.length > 0 ? (
                    <>
                      <h6
                        style={{
                          fontSize: "0.8125rem",
                          fontWeight: 600,
                          color: "#495057",
                          marginTop: showAddForm ? 12 : 0,
                          marginBottom: 8,
                          letterSpacing: "0.02em",
                          textTransform: "uppercase",
                        }}
                      >
                        Existing Art Works ({artWork.length})
                      </h6>
                      <div
                        style={{
                          overflowX: "auto",
                          border: "1px solid #e9ecef",
                          borderRadius: 6,
                          WebkitOverflowScrolling: "touch",
                        }}
                      >
                        <table
                          className="table table-sm table-striped mb-0"
                          style={{
                            fontSize: "0.8125rem",
                            verticalAlign: "middle",
                            marginBottom: 0,
                          }}
                        >
                          <thead>
                            <tr>
                              <th scope="col" style={{ whiteSpace: "nowrap", padding: "8px 10px" }}>
                                Sr.No
                              </th>
                              <th scope="col" style={{ whiteSpace: "nowrap", padding: "8px 10px" }}>
                                Image
                              </th>
                              <th scope="col" style={{ minWidth: 100, padding: "8px 10px" }}>
                                Title
                              </th>
                              <th scope="col" style={{ minWidth: 120, padding: "8px 10px" }}>
                                Description
                              </th>
                              <th scope="col" style={{ whiteSpace: "nowrap", padding: "8px 10px" }}>
                                Rate
                              </th>
                              <th scope="col" style={{ whiteSpace: "nowrap", padding: "8px 10px" }}>
                                Seq.
                              </th>
                              <th scope="col" style={{ minWidth: 120, maxWidth: 180, padding: "8px 10px" }}>
                                Keyword
                              </th>
                              <th scope="col" style={{ whiteSpace: "nowrap", padding: "8px 10px" }}>
                                Status
                              </th>
                              <th scope="col" style={{ whiteSpace: "nowrap", padding: "8px 10px" }}>
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody id="tableBody">
                            {artWork.map((artWorkItem, index) => (
                              <tr key={artWorkItem.id}>
                                <th scope="row" style={{ padding: "6px 10px" }}>
                                  {index + 1}
                                </th>
                                <td style={{ padding: "6px 10px" }}>
                                  {artWorkItem.photo ? (
                                    <img
                                      src={`${API_URL}/media/${artWorkItem.photo}`}
                                      style={{
                                        width: 56,
                                        height: 56,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                        border: "1px solid #eee",
                                      }}
                                      alt={artWorkItem.title || "Art Work"}
                                    />
                                  ) : (
                                    <i
                                      className="bi bi-folder"
                                      style={{ fontSize: "20px", color: "#adb5bd" }}
                                    ></i>
                                  )}
                                </td>
                                <td style={{ padding: "6px 10px" }}>
                                  <span style={{ fontWeight: 600 }}>
                                    {(artWorkItem.title || "No Title").slice(0, 50)}
                                    {(artWorkItem.title && artWorkItem.title.length > 50) ? "…" : ""}
                                  </span>
                                </td>
                                <td style={{ padding: "6px 10px" }}>
                                  <small className="text-muted" style={{ lineHeight: 1.35 }}>
                                    {(artWorkItem.description || "No Description").slice(0, 50)}
                                    {(artWorkItem.description && artWorkItem.description.length > 50) ? "…" : ""}
                                  </small>
                                </td>
                                <td style={{ padding: "6px 10px" }}>
                                  <span style={{ fontWeight: 600 }}>{artWorkItem.rate || "No Rate"}</span>
                                </td>
                                <td style={{ padding: "6px 10px" }}>
                                  <span style={{ fontWeight: 600 }}>{artWorkItem.sequence || "No Sequence"}</span>
                                </td>
                                <td style={{ padding: "6px 10px", maxWidth: 180 }}>
                                  <small
                                    style={{
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      maxWidth: "100%",
                                    }}
                                    title={artWorkItem.keywords || ""}
                                  >
                                    {artWorkItem.keywords || "-"}
                                  </small>
                                </td>
                                <td style={{ padding: "6px 10px" }}>
                                  <span
                                    className="badge"
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "0.75rem",
                                      padding: "4px 8px",
                                      background:
                                        artWorkItem.status === 1
                                          ? "#d1e7dd"
                                          : artWorkItem.status === 0
                                            ? "#fff3cd"
                                            : artWorkItem.status === -1
                                              ? "#f8d7da"
                                              : "#e9ecef",
                                      color:
                                        artWorkItem.status === 1
                                          ? "#0f5132"
                                          : artWorkItem.status === 0
                                            ? "#664d03"
                                            : artWorkItem.status === -1
                                              ? "#842029"
                                              : "#495057",
                                    }}
                                  >
                                    {artWorkItem.status === 1
                                      ? "Approve"
                                      : artWorkItem.status === 0
                                        ? "Pending"
                                        : artWorkItem.status === -1
                                          ? "Reject"
                                          : "No Status"}
                                  </span>
                                </td>
                                <td style={{ padding: "6px 10px" }}>
                                  <div className="social-links d-flex align-items-center gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-link p-0"
                                      onClick={() => handleClickEdit(artWorkItem.id)}
                                      style={{ textDecoration: "none", lineHeight: 1 }}
                                      aria-label="Edit art work"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-link p-0"
                                      onClick={() => confirmDelete(artWorkItem.id)}
                                      style={{ textDecoration: "none", color: "#dc3545", lineHeight: 1 }}
                                      aria-label="Delete art work"
                                    >
                                      <i className="bi bi-trash3"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <p
                      style={{
                        textAlign: "center",
                        color: "#6c757d",
                        fontSize: "0.9rem",
                        margin: "16px 0 8px",
                      }}
                    >
                      No art work available
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Add_Art_Work;